import { Injectable } from '@angular/core';
import { HttpDashboardService } from '../HttpServices/DashboardHttpService/http-dashboard.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { Audience } from '@models/Post/audience.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  isPostsLoadingSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isPostsLoading$: Observable<boolean> = this.isPostsLoadingSubject.pipe(map(count => count > 0));

  isLoadingSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isLoading$: Observable<boolean> = this.isLoadingSubject.pipe(map(count => count > 0));

  constructor(private httpDashboardService: HttpDashboardService) {
  }

  myFeedPosts(myFeed: boolean, pageNumber: number, pageSize: number): Observable<any> {
    this.isPostsLoadingSubject.next(this.isPostsLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getFeedPosts(myFeed, pageNumber, pageSize).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isPostsLoadingSubject.next(this.isPostsLoadingSubject.getValue() - 1)),
    );
  }

  markAsReadNotification(notificationId: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.markAsReadNotification(notificationId).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getPostImageByPostId(postId: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getPostImageByPostId(postId).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getPostVideoByPostId(postId: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getPostVideoByPostId(postId).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  createReply(id: number, replyText: string, replyParentId?: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.createReply(id, replyText, replyParentId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  createPost(
    posts: string, 
    postsPicture: string, 
    postsPictureFileName: string, 
    postsVideo: string, 
    postsVideoFileName: string, 
    audience: number = Audience.POST, 
    sharpenReference?: string
  ) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.createPost({
      postText: posts,
      postsPicture,
      postsPictureFileName,
      postsVideo,
      postsVideoFileName,
      audience,
      sharpenReference,
    }).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  cleanAllNotifications() {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.clearAllNotifications().pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getAllNotifications() {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getAllNotifications().pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  countNewNotifications() {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.countNewNotifications().pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getPostById(id: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getPostById(id).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getRepliesByPostId(id: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getRepliesByPostId(id).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  createQuestion(posts: string, audience: Audience) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.createQuestion(posts, audience).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  deleteReply(replyId: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.deleteReply(replyId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  deletePost(postId: number) {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.deletePost(postId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getLastActiveGroups() {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getLastActiveGroups().pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }

  getLastCreatedGroups() {
    this.isLoadingSubject.next(this.isLoadingSubject.getValue() + 1);
    return this.httpDashboardService.getLastCreatedGroups().pipe(
      map(response => {
        return response;
      }),
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(this.isLoadingSubject.getValue() - 1)),
    );
  }
}
